import { gql, useQuery } from "urql";
import { useLocale, useTranslations } from "@acto/web-common/locales";
import { useCurrencyContext } from "@acto/taskpane/core/contexts/TenantSettingsContext";
import { Granularity, Kpi, KpiComparisonMode } from "@acto/taskpane/core/domain";
import {
  Granularity as GqlGranularity,
  Kpi as GqlKpi,
  KpiComparisonMode as GqlKpiComparisonMode,
} from "@acto/gql/graphql";
import {
  toGqlGranularity,
  toGqlKpi,
  toGqlKpiComparisonMode,
} from "@acto/taskpane/core/data-access/customers/kpiTransformers";
import { buildKpiTrend, type KpiSummaryValueData, type KpiTrend } from "./buildKpiTrend";
import { selectGranularity } from "./selectGranularity";

export type { KpiTrend } from "./buildKpiTrend";

const KpiTrendQuery = gql`
  query GetFieldSalesKpiTrend(
    $clientId: ID!
    $kpis: [Kpi!]!
    $dateRange: DateRangeInput
    $granularity: Granularity!
    $productGroupId: ID
    $comparisonMode: KpiComparisonMode!
  ) {
    kpiSummaries(
      primaryClientId: $clientId
      kpis: $kpis
      dateRange: $dateRange
      granularity: $granularity
      productGroupId: $productGroupId
      comparisonMode: $comparisonMode
    ) {
      granularity
      values {
        kpi
        totalValue
        timeSeries {
          dataPoints {
            periodStart
            value
          }
        }
        comparison {
          totalValue
          change {
            percentageChange
          }
          range {
            startDate
            endDate
          }
        }
        comparisonTimeSeries {
          dataPoints {
            periodStart
            value
          }
        }
      }
    }
  }
`;

interface KpiTrendQueryData {
  kpiSummaries: { granularity: GqlGranularity; values: KpiSummaryValueData[] } | null;
}

interface KpiTrendQueryVariables {
  clientId: string;
  kpis: GqlKpi[];
  dateRange?: { startDate: string; endDate: string };
  granularity: ReturnType<typeof toGqlGranularity>;
  productGroupId?: string;
  comparisonMode: GqlKpiComparisonMode;
}

export interface KpiSummaries {
  values: KpiSummaryValueData[];
  fetching: boolean;
  stale: boolean;
  granularity: Granularity;
}

/** Pauses while `clientId` is undefined. */
export function useKpiSummaries(
  clientId: string | undefined,
  comparisonMode: KpiComparisonMode,
  kpis: GqlKpi[],
  dateRange?: { startDate: string; endDate: string },
  productGroupId?: string,
): KpiSummaries {
  const granularity = selectGranularity(dateRange);

  const [result] = useQuery<KpiTrendQueryData, KpiTrendQueryVariables>({
    query: KpiTrendQuery,
    variables: {
      clientId: clientId ?? "",
      kpis,
      dateRange: dateRange ?? undefined,
      granularity: toGqlGranularity(granularity),
      productGroupId: productGroupId ?? undefined,
      comparisonMode: toGqlKpiComparisonMode(comparisonMode),
    },
    requestPolicy: "cache-first",
    pause: !clientId,
  });

  return {
    values: result.data?.kpiSummaries?.values ?? [],
    fetching: result.fetching,
    stale: result.stale,
    granularity,
  };
}

export function useKpiTrend(
  clientId: string | undefined,
  comparisonMode: KpiComparisonMode,
  selectedKpi: Kpi = Kpi.REVENUE,
  dateRange?: { startDate: string; endDate: string },
  productGroupId?: string,
  isCustomRange = false,
): KpiTrend {
  const { locale } = useLocale();
  const currency = useCurrencyContext();
  const tKpi = useTranslations("kpi");

  // "Marge" is profit-backed: fetch profit (currency) plus the margin ratio for the annotation.
  const isMargin = selectedKpi === Kpi.MARGIN;
  const displayKpi = isMargin ? Kpi.PROFIT : selectedKpi;
  const kpisToFetch = isMargin ? [GqlKpi.Profit, GqlKpi.Margin] : [toGqlKpi(displayKpi)];

  const { values, fetching, stale, granularity } = useKpiSummaries(
    clientId,
    comparisonMode,
    kpisToFetch,
    dateRange,
    productGroupId,
  );

  return buildKpiTrend({
    values,
    fetching,
    stale,
    selectedKpi,
    comparisonMode,
    dateRange,
    isCustomRange,
    granularity,
    locale,
    currency,
    tKpi,
  });
}
